import http from 'http';
import readline from 'readline';
import open from 'open';
import { SessionManager } from '../utils/session.js';
import { formatSuccess, formatInfo } from '../utils/format.js';
import { cliError } from '../utils/errors.js';
import axios from 'axios';
import { CLI_CONFIG } from '../config.js';

const DEFAULT_FRONTEND_URL = CLI_CONFIG.DEFAULT_FRONTEND_URL;
const DEFAULT_API_URL = CLI_CONFIG.DEFAULT_API_URL;

/**
 * One pasted line from the user: full callback URL, `code` / `token` query fragment,
 * bare authorization code (same hex string as in `?code=` — exchanged via token-exchange),
 * or raw JWT (final session token, three segments).
 * Exported for tests.
 */
export function parsePastedInput(line: string): { code: string | null; token: string | null } {
  const t = line.trim();
  if (!t) {
    return { code: null, token: null };
  }

  if (/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(t)) {
    return { code: null, token: t };
  }

  // Bare CLI auth code (e.g. hex from POST /cli/generate-code). Same value as in ?code= — not a JWT.
  if (/^[a-fA-F0-9]{64}$/.test(t)) {
    return { code: t, token: null };
  }

  try {
    const u = new URL(t);
    if (u.searchParams.has('code') || u.searchParams.has('token') || u.pathname === '/callback') {
      return {
        code: u.searchParams.get('code'),
        token: u.searchParams.get('token'),
      };
    }
  } catch {
    // not a full URL
  }

  const m = t.match(/[?&]code=([^&]+)/);
  const m2 = t.match(/[?&]token=([^&]+)/);
  return {
    code: m ? decodeURIComponent(m[1]) : null,
    token: m2 ? decodeURIComponent(m2[1]) : null,
  };
}

async function completeAuthFromCodeOrToken(
  code: string | null,
  token: string | null,
  baseApiUrl: string,
  apiUrl?: string
): Promise<void> {
  if (code) {
    const response = await axios.post(`${baseApiUrl}/cli/token-exchange`, { code });
    const jwtToken = response.data?.token;
    if (!jwtToken) {
      throw new Error('Server did not return a token');
    }
    SessionManager.saveToken(jwtToken, apiUrl);
    return;
  }
  if (token) {
    SessionManager.saveToken(token, apiUrl);
    return;
  }
  throw new Error('No code or token in input');
}

export async function runInteractiveLogin(apiUrl?: string): Promise<void> {
  const port = CLI_CONFIG.LOCAL_AUTH_PORT;
  const loginUrl = `${DEFAULT_FRONTEND_URL}/cli-auth?port=${port}`;
  const baseApiUrl = apiUrl || DEFAULT_API_URL;

  console.log(formatInfo('Opening HyperTask in your browser...'));

  return new Promise((resolve) => {
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout>;
    let rl: readline.Interface | null = null;

    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timeoutId);
      if (rl) {
        try {
          rl.close();
        } catch {
          // ignore
        }
        rl = null;
      }
      if (server.listening) {
        server.close();
      }
      resolve();
    };

    const server = http.createServer(async (req, res) => {
      res.setHeader('Access-Control-Allow-Origin', '*');
      res.setHeader('Access-Control-Allow-Methods', 'GET, OPTIONS');

      if (req.method === 'OPTIONS') {
        res.writeHead(204);
        res.end();
        return;
      }

      const url = new URL(req.url || '/', `http://localhost:${port}`);

      if (url.pathname === '/callback') {
        if (settled) {
          res.writeHead(200, { 'Content-Type': 'text/plain' });
          res.end('Login already completed.');
          return;
        }

        const code = url.searchParams.get('code');
        const token = url.searchParams.get('token');

        if (code) {
          try {
            if (settled) {
              res.writeHead(200, { 'Content-Type': 'text/html' });
              res.end(getSuccessHtml());
              return;
            }
            console.log('Authorization code received. Securing session...');
            await completeAuthFromCodeOrToken(code, null, baseApiUrl, apiUrl);
            if (settled) {
              res.writeHead(200, { 'Content-Type': 'text/html' });
              res.end(getSuccessHtml());
              return;
            }
            res.writeHead(200, { 'Content-Type': 'text/html' });
            res.end(getSuccessHtml());
            console.log('\n' + formatSuccess('Authentication successful!'));
            console.log(`Token saved to ~/.hypertask/config.json`);
            finish();
          } catch (error: any) {
            if (settled) {
              return;
            }
            res.writeHead(500, { 'Content-Type': 'text/plain' });
            res.end('Failed to exchange code for token. Please close this tab and try again.');
            console.error('\n' + formatInfo(`Failed to authenticate: ${error.message}`));
            finish();
          }
        } else if (token) {
          try {
            if (settled) {
              res.writeHead(200, { 'Content-Type': 'text/html' });
              res.end(getSuccessHtml());
              return;
            }
            await completeAuthFromCodeOrToken(null, token, baseApiUrl, apiUrl);
            if (settled) {
              res.writeHead(200, { 'Content-Type': 'text/html' });
              res.end(getSuccessHtml());
              return;
            }
            res.writeHead(200, { 'Content-Type': 'text/html' });
            res.end(getSuccessHtml());
            console.log('\n' + formatSuccess('Authentication successful!'));
            console.log(`Token saved to ~/.hypertask/config.json`);
            finish();
          } catch (error: any) {
            if (settled) {
              return;
            }
            res.writeHead(500, { 'Content-Type': 'text/plain' });
            res.end('Failed to save token.');
            console.error('\n' + formatInfo(`Failed to authenticate: ${error.message}`));
            finish();
          }
        } else {
          res.writeHead(400, { 'Content-Type': 'text/plain' });
          res.end('No code or token provided');
          console.error('\nFailed to receive authorization code from browser.');
          finish();
        }
      } else {
        res.writeHead(404);
        res.end('Not found');
      }
    });

    const startPasteFallback = () => {
      if (!process.stdin.isTTY || !process.stdout.isTTY) {
        return;
      }

      console.log(
        '\n' +
          formatInfo(
            "If the browser cannot reach this machine (or you're on another host), paste: the full callback URL, or the hex code only (same as in ?code=), or the final JWT — all work."
          )
      );

      rl = readline.createInterface({
        input: process.stdin,
        output: process.stdout,
      });

      const ask = () => {
        if (settled || !rl) {
          return;
        }
        rl.question('Paste callback URL, hex code, or JWT (optional): ', async (line) => {
          if (settled) {
            return;
          }
          const trimmed = line.trim();
          if (!trimmed) {
            if (!settled) {
              console.log(formatInfo('Still waiting for browser or a paste. Try again, or wait for the callback.'));
              ask();
            }
            return;
          }

          try {
            if (settled) {
              return;
            }
            const { code, token } = parsePastedInput(trimmed);
            if (!code && !token) {
              console.log(
                formatInfo(
                  'Could not find a URL, hex code, token, or JWT in that input. Try again, or wait for the browser.'
                )
              );
              ask();
              return;
            }
            if (settled) {
              return;
            }
            await completeAuthFromCodeOrToken(code, token, baseApiUrl, apiUrl);
            if (settled) {
              return;
            }
            console.log('\n' + formatSuccess('Authentication successful!'));
            console.log(`Token saved to ~/.hypertask/config.json`);
            finish();
          } catch (e: any) {
            console.error(formatInfo(e?.message || String(e)));
            if (!settled) {
              ask();
            }
          }
        });
      };
      ask();
    };

    server.listen(port, async () => {
      console.log(`Waiting for authentication...`);
      console.log(`(If the browser doesn't open automatically, use this link: ${loginUrl})`);
      startPasteFallback();

      try {
        await open(loginUrl);
      } catch (err) {
        console.log('\nCould not open browser automatically. Open the link above manually.');
      }
    });

    timeoutId = setTimeout(() => {
      if (settled) {
        return;
      }
      cliError('\nLogin timed out after 3 minutes. Please try again.');
      finish();
    }, 3 * 60 * 1000);
  });
}

function getSuccessHtml() {
  return `
    <html>
      <head>
        <style>
          body { font-family: system-ui, sans-serif; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; background: #0f172a; color: white; }
          .box { background: #1e293b; padding: 2rem; border-radius: 8px; border: 1px solid #334155; text-align: center; }
          h1 { color: #22c55e; }
        </style>
      </head>
      <body>
        <div class="box">
          <h1>Login Successful!</h1>
          <p>You can close this window and return to your terminal.</p>
          <script>setTimeout(() => window.close(), 2000);</script>
        </div>
      </body>
    </html>
  `;
}
